import { useEffect, useState, useRef } from "react";
import {
  Wrench,
  AlertTriangle,
  Clock,
  CheckCircle2,
  Phone,
  Upload,
  X,
  FileImage,
  FileVideo,
  Loader2,
  Shield,
  Home,
  Users,
  Building2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { TenantsSEO } from "../components/SEO";
import api from "../lib/api";
import { openWhatsAppEnquiry } from "../lib/whatsappNotify";

const ICONS = {
  Wrench,
  AlertTriangle,
  Clock,
  CheckCircle2,
  Phone,
  Shield,
  Home,
  Users,
  Building2,
};

const URGENCY_STYLE = {
  emergency: { icon: "AlertTriangle", color: "text-red-600", bg: "bg-red-50" },
  urgent: { icon: "Clock", color: "text-orange-600", bg: "bg-orange-50" },
  routine: { icon: "Wrench", color: "text-blue-600", bg: "bg-blue-50" },
};

const DEFAULTS = {
  hero_eyebrow: "For Tenants",
  hero_title: "Tenant Support",
  hero_subtitle:
    "We're here to help make renting straightforward. If you need to report a maintenance issue, use the form below and we'll log your request and respond as soon as possible.",
  hero_cta_text: "Report a Maintenance Issue",
  tenant_info_items: [
    { icon: "CheckCircle2", title: "Professional Management", description: "Your property is managed by a dedicated team committed to maintaining high standards." },
    { icon: "Wrench", title: "Responsive Maintenance", description: "We coordinate repairs quickly with our network of trusted local contractors." },
    { icon: "Phone", title: "Clear Communication", description: "We're always available to answer questions and address concerns." },
  ],
  form_section_title: "Report a Maintenance Issue",
  form_section_description:
    "Please provide as much detail as possible about the issue. Upload photos or videos to help us understand and resolve your problem faster.",
  urgency_guide_title: "Urgency Guide",
  urgency_guide_intro:
    "Please select the appropriate urgency level for your issue to help us respond appropriately.",
  urgency_items: [
    { level: "emergency", title: "Emergency", description: "No power, serious leak, security risk, no heating in winter, gas leak" },
    { level: "urgent", title: "Urgent", description: "Heating issues (non-emergency), blocked drains, broken lock" },
    { level: "routine", title: "Routine", description: "Minor repairs, appliance issues, general maintenance" },
  ],
  emergency_card_title: "For True Emergencies",
  emergency_card_text:
    "If you have an emergency outside office hours (gas leak, flood, break-in), please call us immediately:",
  emergency_phone: "07586 890299",
  office_phone: "01924 520163",
};

const TenantsPage = () => {
  const [content, setContent] = useState(DEFAULTS);
  const [submitting, setSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    tenant_name: "",
    tenant_email: "",
    tenant_phone: "",
    property_address: "",
    issue_description: "",
    urgency: "",
  });

  useEffect(() => {
    let mounted = true;
    api
      .getContentSection("tenants_page")
      .then((res) => {
        if (mounted && res?.content) {
          setContent({ ...DEFAULTS, ...res.content });
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === "development") {
          console.warn("Failed to load tenants_page CMS content, using defaults:", err?.message);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  const openWhatsAppNotification = ({
    tenant_name,
    tenant_email,
    tenant_phone,
    property_address,
    urgency,
    issue_description,
  }) => {
    openWhatsAppEnquiry({
      name: tenant_name,
      email: tenant_email,
      phone: tenant_phone,
      submission_type: "maintenance",
      subject: `Maintenance Request — ${(urgency || "unknown").toUpperCase()}`,
      reference: property_address,
      body: issue_description,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.urgency) {
      toast.error("Please select an urgency level");
      return;
    }
    setSubmitting(true);
    try {
      await api.submitMaintenanceWithFiles(formData, uploadedFiles);
      toast.success("Maintenance request submitted successfully! We'll confirm receipt and advise next steps.");
      openWhatsAppNotification(formData);
      setFormData({
        tenant_name: "",
        tenant_email: "",
        tenant_phone: "",
        property_address: "",
        issue_description: "",
        urgency: "",
      });
      setUploadedFiles([]);
    } catch (error) {
      toast.error("Failed to submit request. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 50 * 1024 * 1024;
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "video/mp4", "video/quicktime", "video/x-msvideo", "video/webm"];

    const validFiles = files.filter((file) => {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Invalid file type. Please upload images or videos.`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name}: File too large. Maximum size is 50MB.`);
        return false;
      }
      return true;
    });

    if (uploadedFiles.length + validFiles.length > 5) {
      toast.error("Maximum 5 files allowed");
      return;
    }

    setUploadedFiles((prev) => [...prev, ...validFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith("video/")) {
      return <FileVideo className="w-5 h-5 text-purple-600" />;
    }
    return <FileImage className="w-5 h-5 text-blue-600" />;
  };

  const renderIcon = (name, className) => {
    const Icon = ICONS[name] || CheckCircle2;
    return <Icon className={className} />;
  };

  const emergencyPhoneDigits = (content.emergency_phone || "").replace(/\s/g, "");

  return (
    <div data-testid="tenants-page">
      <TenantsSEO />
      {/* Hero Section */}
      <section className="bg-navy text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-gold font-medium text-sm tracking-wider uppercase mb-4 block">
              {content.hero_eyebrow}
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight mb-6">
              {content.hero_title}
            </h1>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">{content.hero_subtitle}</p>
            <Button
              onClick={() => document.getElementById("maintenance-form").scrollIntoView({ behavior: "smooth" })}
              size="lg"
              className="bg-gold hover:bg-gold-light text-navy font-semibold"
              data-testid="tenants-cta-report"
            >
              <Wrench className="w-5 h-5 mr-2" />
              {content.hero_cta_text}
            </Button>
          </div>
        </div>
      </section>

      {/* Tenant Info Section */}
      <section className="py-16 bg-off-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8" data-testid="tenants-info-grid">
            {(content.tenant_info_items || []).map((item, index) => (
              <div key={item.title || index} className="flex gap-4">
                <div className="w-12 h-12 rounded-lg bg-navy/5 flex items-center justify-center flex-shrink-0">
                  {renderIcon(item.icon, "w-6 h-6 text-navy")}
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-navy mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Maintenance Form Section */}
      <section id="maintenance-form" className="py-20 md:py-28 bg-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/10 mb-6">
              <Wrench className="w-8 h-8 text-gold" />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-4">
              {content.form_section_title}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">{content.form_section_description}</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <div className="bg-slate-50 rounded-2xl p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">Your Name *</label>
                    <Input
                      value={formData.tenant_name}
                      onChange={(e) => setFormData({ ...formData, tenant_name: e.target.value })}
                      required
                      data-testid="maintenance-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">Email Address *</label>
                    <Input
                      type="email"
                      value={formData.tenant_email}
                      onChange={(e) => setFormData({ ...formData, tenant_email: e.target.value })}
                      required
                      data-testid="maintenance-email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Phone Number</label>
                  <Input
                    type="tel"
                    value={formData.tenant_phone}
                    onChange={(e) => setFormData({ ...formData, tenant_phone: e.target.value })}
                    data-testid="maintenance-phone"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Property Address *</label>
                  <Input
                    value={formData.property_address}
                    onChange={(e) => setFormData({ ...formData, property_address: e.target.value })}
                    placeholder="Full address of the rental property"
                    required
                    data-testid="maintenance-address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Urgency Level *</label>
                  <Select value={formData.urgency} onValueChange={(v) => setFormData({ ...formData, urgency: v })}>
                    <SelectTrigger data-testid="maintenance-urgency">
                      <SelectValue placeholder="Select urgency level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="emergency">Emergency</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="routine">Routine</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Issue Description *</label>
                  <Textarea
                    rows={5}
                    value={formData.issue_description}
                    onChange={(e) => setFormData({ ...formData, issue_description: e.target.value })}
                    placeholder="Please describe the issue in detail, including when it started and any relevant information"
                    required
                    data-testid="maintenance-description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-navy mb-2">
                    Upload Photos/Videos (Optional)
                  </label>
                  <p className="text-slate-500 text-sm mb-3">
                    Adding photos or videos helps us understand and resolve your issue faster.
                  </p>

                  <div
                    className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-navy transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,video/*"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="maintenance-file-input"
                    />
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-navy font-medium">Click to upload images or videos</p>
                    <p className="text-slate-500 text-sm mt-1">JPG, PNG, MP4, MOV up to 50MB each (max 5 files)</p>
                  </div>

                  {uploadedFiles.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-sm font-medium text-navy">{uploadedFiles.length} file(s) selected:</p>
                      {uploadedFiles.map((file, index) => (
                        <div key={`${file.name}-${file.lastModified}-${file.size}`} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            {getFileIcon(file)}
                            <div>
                              <p className="text-sm font-medium text-navy truncate max-w-[200px]">{file.name}</p>
                              <p className="text-xs text-slate-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="p-1 hover:bg-red-100 rounded-full transition-colors"
                          >
                            <X className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full bg-navy hover:bg-navy-light text-lg py-6"
                  disabled={submitting}
                  data-testid="maintenance-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Maintenance Request"
                  )}
                </Button>
              </form>
            </div>

            {/* Urgency Guide */}
            <div>
              <h2 className="font-heading text-3xl font-semibold text-navy mb-6">
                {content.urgency_guide_title}
              </h2>
              <p className="text-slate-600 mb-8">{content.urgency_guide_intro}</p>

              <div className="space-y-4" data-testid="urgency-cards">
                {(content.urgency_items || []).map((item, index) => {
                  const style = URGENCY_STYLE[item.level] || URGENCY_STYLE.routine;
                  return (
                    <Card key={item.level || index} className={`border-slate-200 ${style.bg}`}>
                      <CardContent className="p-6">
                        <div className="flex gap-4">
                          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
                            {renderIcon(style.icon, `w-5 h-5 ${style.color}`)}
                          </div>
                          <div>
                            <h3 className={`font-heading font-semibold ${style.color} mb-1`}>{item.title}</h3>
                            <p className="text-slate-600 text-sm">{item.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <Card className="mt-8 border-navy bg-navy/5">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-navy mb-2">
                    {content.emergency_card_title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">{content.emergency_card_text}</p>
                  <a
                    href={`tel:${emergencyPhoneDigits}`}
                    className="inline-flex items-center gap-2 text-navy font-semibold hover:text-gold transition-colors"
                    data-testid="tenants-emergency-phone"
                  >
                    <Phone className="w-5 h-5" />
                    {content.emergency_phone}
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TenantsPage;
